import type { InputSignal, Provider, Type } from '@angular/core';

import { createProviderToken } from '@core/common-legacy';

export type DeepPartial<T> =
  T extends ReadonlyArray<infer U>
    ? ReadonlyArray<DeepPartial<U>>
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T;

export interface ChatPresenterComponent<TArgs> {
  readonly args: InputSignal<TArgs>;
}

/**
 * Renders a backend render-tool inline in the assistant chat panel.
 *
 * - **Strict (default)**: presenter mounts only after the `tool-call` event
 *   fires with fully-formed, BE-validated args. Component declares
 *   `input.required<TArgs>()`.
 * - **Progressive (`progressive: true`)**: presenter mounts during streaming
 *   and may receive partial args (some required fields may be missing).
 *   Component declares `input.required<DeepPartial<TArgs> | undefined>()`
 *   and is responsible for handling missing fields with skeletons / placeholders.
 */
export type RenderPresenter<TArgs = unknown> =
  | {
      readonly id: string;
      readonly progressive?: false;
      readonly component: Type<ChatPresenterComponent<TArgs>>;
    }
  | {
      readonly id: string;
      readonly progressive: true;
      readonly component: Type<ChatPresenterComponent<DeepPartial<TArgs> | undefined>>;
    };

const renderPresenterToken = createProviderToken<RenderPresenter>(
  'TELLENT_ASSISTANT_RENDER_PRESENTERS',
)(true);

export const injectTellentAssistantRenderPresenters = renderPresenterToken.inject;

export function provideTellentAssistantRenderPresenter<TArgs>(
  presenter: RenderPresenter<TArgs>,
): Provider {
  return renderPresenterToken.provide({ useValue: <RenderPresenter>presenter });
}
